import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { formatAed, formatMoney, formatSigned } from '../lib/money'

export function Money({
  fils,
  currency = false,
  signed = false,
  className = '',
}: {
  fils: number
  currency?: boolean
  signed?: boolean
  className?: string
}) {
  const text = signed ? formatSigned(fils, currency) : currency ? formatAed(fils) : formatMoney(fils)
  const tone = fils < 0 ? 'negative' : signed && fils > 0 ? 'positive' : ''
  return <span className={`tnum ${tone} ${className}`.trim()}>{text}</span>
}

export function Card({
  children,
  tight = false,
  className = '',
  style,
}: {
  children: ReactNode
  tight?: boolean
  className?: string
  style?: CSSProperties
}) {
  return (
    <div className={`card ${tight ? 'tight' : ''} ${className}`.trim()} style={style}>
      {children}
    </div>
  )
}

/**
 * A text field that saves when you leave it, not on every keystroke.
 *
 * Saving per character means a burst of writes racing each other, and since each save
 * carries the version it was based on, the later ones come back as conflicts and the whole
 * screen reloads mid-word. Committing on blur avoids the race rather than papering over it.
 */
export function CommittedInput({
  value,
  onCommit,
  placeholder,
  style,
  maxLength,
}: {
  value: string
  onCommit: (value: string) => void
  placeholder?: string
  style?: CSSProperties
  maxLength?: number
}) {
  const [draft, setDraft] = useState(value)

  // Follow the stored value when it changes elsewhere — a push from the other account,
  // say — but never while the field has focus, or it would overwrite what is being typed.
  const focused = useRef(false)
  useEffect(() => {
    if (!focused.current) setDraft(value)
  }, [value])

  return (
    <input
      type="text"
      value={draft}
      placeholder={placeholder}
      style={style}
      maxLength={maxLength}
      onFocus={() => {
        focused.current = true
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        focused.current = false
        if (draft !== value) onCommit(draft)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
    />
  )
}

export function Chip({
  children,
  tone = '',
}: {
  children: ReactNode
  tone?: '' | 'mint' | 'coral' | 'amber' | 'indigo' | 'lilac' | 'rose'
}) {
  return <span className={`chip ${tone}`.trim()}>{children}</span>
}

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      className={`switch ${checked ? 'on' : ''}`}
      aria-label={label}
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
    />
  )
}

export function Sheet({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div className="scrim" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        {children}
      </div>
    </div>
  )
}

/**
 * Used for anything irreversible: buying over budget, wiping data, and claiming an earn
 * action — a stray tap should never move money.
 */
export function Dialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  danger = false,
  cancelLabel = 'Cancel',
}: {
  title: string
  body: ReactNode
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  danger?: boolean
  cancelLabel?: string
}) {
  return (
    <div className="scrim" style={{ alignItems: 'center' }} onClick={onCancel}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <div className="muted" style={{ fontSize: 14 }}>
          {body}
        </div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="btn ghost" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? 'btn danger' : 'btn'}
            onClick={onConfirm}
            style={danger ? { fontWeight: 600 } : undefined}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export function TopBar({
  title,
  subtitle,
  onBack,
  actions,
}: {
  title: string
  subtitle?: string
  onBack?: () => void
  actions?: ReactNode
}) {
  return (
    <div className="topbar">
      {onBack ? (
        <button type="button" className="icon-btn" onClick={onBack} aria-label="Back">
          ‹
        </button>
      ) : null}
      <div className="grow">
        <h1>{title}</h1>
        {subtitle ? <p className="sub">{subtitle}</p> : null}
      </div>
      {actions}
    </div>
  )
}

export function StatTile({
  label,
  value,
  glyph,
  tone,
}: {
  label: string
  value: string
  glyph: string
  tone: string
}) {
  return (
    <div className="stat">
      <div className="grow">
        <div className="label">{label}</div>
        <div className="stat-value">{value}</div>
      </div>
      <div className="tile-glyph" style={{ background: tone }} aria-hidden>
        {glyph}
      </div>
    </div>
  )
}

export function Bar({ fraction }: { fraction: number }) {
  const safe = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0))
  return (
    <div className="bar">
      <span style={{ width: `${safe * 100}%` }} />
    </div>
  )
}

export function Empty({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <Card>
      <div style={{ textAlign: 'center', padding: '18px 6px' }}>
        <div style={{ fontWeight: 650, marginBottom: 4 }}>{title}</div>
        <div className="muted" style={{ fontSize: 14 }}>
          {body}
        </div>
        {action ? <div style={{ marginTop: 14 }}>{action}</div> : null}
      </div>
    </Card>
  )
}
